import { z } from "zod";
import catalogSource from "./packaging-product-catalog.json" with { type: "json" };
import type { PackagingPlanRow } from "./packaging-report-types.js";

const catalogSchema = z.object({
  schemaVersion: z.literal(1),
  source: z.object({ fileName: z.string().min(1), sheet: z.literal("Veri - Ürün"), sha256: z.string().regex(/^[a-f0-9]{64}$/u), sourceIssues: z.array(z.object({ cell: z.string().regex(/^A\d+$/u), value: z.string().min(1), reason: z.string().min(1) })) }),
  products: z.array(z.object({ productCode: z.string().regex(/^\d{8}$/u), mainGroup: z.string().min(1), subGroup: z.string().min(1).nullable(), subdivision: z.string().min(1).nullable() })).min(1),
}).superRefine((value, context) => {
  const codes = new Set<string>();
  value.products.forEach((product, index) => {
    if (codes.has(product.productCode)) context.addIssue({ code: "custom", path: ["products", index, "productCode"], message: "Stok kodu katalogda tekrar edemez." });
    codes.add(product.productCode);
  });
});
const catalog = catalogSchema.parse(catalogSource);
const byCode = new Map(catalog.products.map((product) => [product.productCode, product]));

export const UNKNOWN_MAIN_GROUP = "Grubu tanımlanmamış";
export const UNSPECIFIED_SUB_GROUP = "Alt grup belirtilmemiş";
export const UNSPECIFIED_SUBDIVISION = "Alt kırılım belirtilmemiş";
export type PackagingProductFilters = { mainGroup: string; subGroup: string; subdivision: string };
export const packagingProductFiltersSchema = z.object({ mainGroup: z.string().max(160).default(""), subGroup: z.string().max(160).default(""), subdivision: z.string().max(160).default("") }).superRefine((value, context) => {
  if (value.subGroup && !value.mainGroup) context.addIssue({ code: "custom", path: ["subGroup"], message: "Alt grup için ürün grubu gerekli." });
  if (value.subdivision && !value.subGroup) context.addIssue({ code: "custom", path: ["subdivision"], message: "Alt kırılım için alt grup gerekli." });
});
export const emptyPackagingProductFilters = (): PackagingProductFilters => ({ mainGroup: "", subGroup: "", subdivision: "" });
export type PackagingProductClassification = { mainGroup: string; subGroup: string; subdivision: string };

export function classifyPackagingProduct(productCode: string): PackagingProductClassification {
  const product = byCode.get(productCode.trim());
  return product ? { mainGroup: product.mainGroup, subGroup: product.subGroup ?? UNSPECIFIED_SUB_GROUP, subdivision: product.subdivision ?? UNSPECIFIED_SUBDIVISION }
    : { mainGroup: UNKNOWN_MAIN_GROUP, subGroup: UNSPECIFIED_SUB_GROUP, subdivision: UNSPECIFIED_SUBDIVISION };
}
export function matchesPackagingProductFilters(row: PackagingPlanRow, filters: PackagingProductFilters): boolean {
  return matchesPackagingProductCodeFilters(row.productCode, filters);
}
export function matchesPackagingProductCodeFilters(productCode: string, filters: PackagingProductFilters): boolean {
  const classification = classifyPackagingProduct(productCode);
  return (!filters.mainGroup || classification.mainGroup === filters.mainGroup)
    && (!filters.subGroup || classification.subGroup === filters.subGroup)
    && (!filters.subdivision || classification.subdivision === filters.subdivision);
}
function sort(values: Iterable<string>) { return [...new Set(values)].sort((left, right) => left.localeCompare(right, "tr-TR")); }
export function packagingProductFilterOptions(rows: Iterable<PackagingPlanRow>, filters: PackagingProductFilters) {
  const classifications = [...rows].map((row) => classifyPackagingProduct(row.productCode));
  const byMain = filters.mainGroup ? classifications.filter((item) => item.mainGroup === filters.mainGroup) : [];
  const bySub = filters.mainGroup && filters.subGroup ? byMain.filter((item) => item.subGroup === filters.subGroup) : [];
  const hasSubdivision = bySub.some((item) => item.subdivision !== UNSPECIFIED_SUBDIVISION);
  return {
    mainGroups: sort(classifications.map((item) => item.mainGroup)),
    subGroups: sort(byMain.map((item) => item.subGroup)),
    subdivisions: hasSubdivision ? sort(bySub.map((item) => item.subdivision)) : [],
  };
}
export function packagingProductFilterScope(filters: PackagingProductFilters): string[] {
  return [filters.mainGroup && `Ürün grubu: ${filters.mainGroup}`, filters.subGroup && `Alt grup: ${filters.subGroup}`, filters.subdivision && `Alt kırılım: ${filters.subdivision}`].filter((value): value is string => Boolean(value));
}
export const packagingProductCatalog = catalog;
